import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Card,
  Input,
  InputHelperText,
  InputLabel,
  InputRoot,
  Switch,
  SwitchLabel,
  typographyVariants,
} from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { type FlowFormInput } from '../../types/singleFlow.js';

const ApprovedRequestNotification = () => {
  const { t } = useTranslation('@myn/approval_v2');
  const isReadOnly = !useIsAllowedPermission('approvalFlowFeature:modify');
  const {
    control,
    register,
    trigger,
    formState: { errors },
  } = useFormContext<FlowFormInput>();

  const isNotificationEnabled = useWatch({
    control,
    name: 'approvedRequestNotification.enabled',
  });

  const recipientEmailsErrorMessage =
    errors.approvedRequestNotification?.recipientEmails?.message;
  const recipientEmailsFieldDisabled = !isNotificationEnabled || isReadOnly;

  return (
    <Card className="w-full" hover="noHover">
      <div className="grid gap-4">
        <div className="flex gap-3">
          <h6 className={cn(typographyVariants({ variant: 'h6' }), 'grow')}>
            {t('Send on approval')}
          </h6>
          <Controller
            control={control}
            name="approvedRequestNotification.enabled"
            render={({ field }) => {
              const handleClick = () => {
                field.onChange(!field.value);
                trigger('approvedRequestNotification.recipientEmails');
              };
              return (
                <SwitchLabel
                  htmlFor="approved-request-notification-check"
                  className={typographyVariants({
                    variant: 'bodyShort01',
                    color: 'secondary',
                  })}
                >
                  <Switch
                    data-dd-action-name="Send on approval: toggle"
                    id="approved-request-notification-check"
                    size="small"
                    checked={field.value}
                    onClick={handleClick}
                    disabled={isReadOnly}
                  />
                  {field.value ? t('Enable') : t('Disable')}
                </SwitchLabel>
              );
            }}
          />
        </div>
        <p
          className={typographyVariants({
            variant: 'bodyLong01',
            color: 'secondary',
          })}
        >
          {t(
            'Specify the email addresses that should receive the exported Buying Plan once it has been approved.',
          )}
        </p>
        <InputRoot>
          <InputLabel
            htmlFor="approved-request-notification-recipients"
            label={t('Email addresses')}
            disabled={recipientEmailsFieldDisabled}
          />
          <Input
            {...register('approvedRequestNotification.recipientEmails')}
            data-dd-action-name="Recipient emails input field"
            id="approved-request-notification-recipients"
            placeholder={t('Enter')}
            className="w-full"
            autoComplete="off"
            color={recipientEmailsErrorMessage ? 'error' : 'default'}
            disabled={recipientEmailsFieldDisabled}
          />
          {recipientEmailsErrorMessage ? (
            <InputHelperText
              color="error"
              helperText={recipientEmailsErrorMessage}
            />
          ) : null}
        </InputRoot>
      </div>
    </Card>
  );
};

export default ApprovedRequestNotification;
